import { useEffect, useState } from 'react';
import { X, Copy, PlugZap } from 'lucide-react';
import { useMcpTab, requestPairCode } from '../agent/mcpTab';
import { proxyHttpBase } from '../agent/config';

interface Props {
  onClose: () => void;
}

/**
 * Modal dialog showing the MCP pairing code so the user can connect an
 * external agent to this canvas session.
 *
 * NOTE: onClose does NOT call disconnectMcpTab(). The WebSocket connection
 * must stay open so the paired session remains live after the user closes
 * this dialog. Disconnecting here would drop the TabSession and break any
 * agent currently using it.
 */
export function ConnectAgent({ onClose }: Props) {
  const status = useMcpTab((s) => s.status);
  const code = useMcpTab((s) => s.code);
  const expiresAt = useMcpTab((s) => s.expiresAt);
  const error = useMcpTab((s) => s.error);
  const [copied, setCopied] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Initiate pairing on mount. The store reflects progress;
  // the catch is only to prevent an unhandled rejection — the error message
  // is surfaced through the store's `error` field.
  useEffect(() => {
    void requestPairCode().catch(() => {});
  }, []);

  // Escape to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleCopy = () => {
    if (!code) return;
    void navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRetry = () => {
    if (retrying) return;
    setRetrying(true);
    // requestPairCode throws if a request is already in-flight; catch + ignore
    // that case since the store will reflect the result when it lands.
    void requestPairCode().catch(() => {}).finally(() => setRetrying(false));
  };

  // Derive the MCP server URL: ws→http, wss→https, strip query, append /mcp.
  const mcpUrl = `${proxyHttpBase()}/mcp`;

  // Compute human-readable expiry ("~N min").
  const expiryLabel = expiresAt
    ? `~${Math.max(1, Math.round((expiresAt - Date.now()) / 60_000))} min`
    : null;

  return (
    // Scrim — click to close. Intentionally does NOT disconnect (see note above).
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={onClose}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="flex flex-col gap-4"
        style={{
          width: 'min(440px, 92vw)',
          background: 'var(--color-surface)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 12,
          padding: 20,
          boxShadow: 'var(--shadow-2)',
          fontFamily: 'var(--font-sans)',
          fontSize: 13,
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-semibold text-sm">
            <PlugZap size={14} />
            Connect agent
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--color-menu-hover)] flex items-center"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        {status === 'connecting' && (
          <p className="text-[var(--color-text-muted)] text-xs">Connecting…</p>
        )}

        {status === 'connected' && code && (
          <>
            {/* Pairing code */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">Pairing code</span>
              <div
                className="flex items-center gap-2 px-3 py-2 rounded-[6px] border border-[var(--color-border)] bg-[var(--color-surface-2)]"
              >
                <span
                  className="flex-1 text-lg tracking-widest select-all"
                  style={{ fontFamily: 'var(--font-mono)' }}
                  data-testid="pair-code"
                >
                  {code}
                </span>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 px-2 py-1 rounded-[6px] text-xs hover:bg-[var(--color-menu-hover)]"
                  title="Copy code"
                  aria-label="Copy pairing code"
                >
                  <Copy size={12} />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              {expiryLabel && (
                <span className="text-[11px] text-[var(--color-text-muted)]">
                  Expires in {expiryLabel}
                </span>
              )}
            </div>

            {/* MCP server URL */}
            <div className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">MCP server URL</span>
              <div
                className="px-3 py-2 rounded-[6px] border border-[var(--color-border)] bg-[var(--color-surface-2)] text-xs break-all"
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                {mcpUrl}
              </div>
            </div>

            {/* Instructions */}
            <p className="text-xs text-[var(--color-text-muted)] leading-relaxed">
              Add this server to your agent, then call its{' '}
              <code style={{ fontFamily: 'var(--font-mono)' }} className="text-[var(--color-text)]">pair</code>{' '}
              tool with the code above.
            </p>
          </>
        )}

        {status === 'error' && (
          <div className="flex flex-col gap-2">
            <p className="text-xs" style={{ color: 'var(--color-danger)' }}>
              {error ?? 'Connection failed.'}
            </p>
            <button
              onClick={handleRetry}
              disabled={retrying}
              className="self-start px-3 py-1.5 rounded-[6px] border border-[var(--color-border)] text-xs hover:bg-[var(--color-menu-hover)] disabled:opacity-30"
            >
              Retry
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
